import { Alert02Icon, CheckmarkCircle02Icon } from '@hugeicons/core-free-icons';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { CustomerFilter } from '@/components/audit/customer-filter';
import { AuditTable } from '@/components/audit-table';
import { ExportButton } from '@/components/export-button';
import { PageHeader } from '@/components/page-header';
import { Icon } from '@/components/primitives/icon';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Select } from '@/components/ui/select';
import { actionOptions } from '@/lib/audit-text';
import { relativeTime } from '@/lib/format';
import { adminQuery, useKeysetList } from '@/lib/query';
import { useCan, useHas } from '@/lib/staff-context';
import { cn } from '@/lib/utils';

const searchSchema = z.object({
  staff_id: z.string().uuid().optional(),
  customer_id: z.string().uuid().optional(),
  action: z.string().max(80).optional(),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /** Routine reads (page views and lookups) show only when this is `all`. */
  reads: z.enum(['all']).optional(),
});

type AuditSearch = z.infer<typeof searchSchema>;

/** Filters live in the URL, so a filtered view can be shared (decision 0007). */
export const Route = createFileRoute('/_app/audit')({
  validateSearch: searchSchema,
  component: AuditPage,
});

function toArgs(search: AuditSearch) {
  return {
    hide_reads: search.reads !== 'all',
    staff_id: search.staff_id,
    customer_id: search.customer_id,
    action: search.action,
    from: search.from ? `${search.from}T00:00:00Z` : undefined,
    to: search.to ? `${search.to}T23:59:59.999Z` : undefined,
    limit: 50,
  };
}

function AuditPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const seesAll = useHas('audit.all');
  const canVerify = useCan('audit.verify');
  const canListStaff = useCan('staff.list');
  const args = toArgs(search);
  const page = useKeysetList('audit.list', args, {
    rows: (data) => data.rows,
    cursor: { arg: 'before_id', next: 'next_before_id' },
  });
  const staff = useQuery({
    ...adminQuery('staff.list', {}),
    enabled: seesAll && canListStaff,
  });
  const [draft, setDraft] = useState(search);
  const actions = useMemo(() => actionOptions(), []);
  const known = actions.some((option) => option.value === draft.action);

  return (
    <>
      <PageHeader
        title="Audit log"
        description={
          seesAll
            ? 'Every staff action, reveal, export and page opened. Entries cannot be changed or removed.'
            : 'Your own actions in the portal. Entries cannot be changed or removed.'
        }
        actions={
          seesAll ? <ExportButton source="audit.list" args={args} /> : null
        }
      />
      {seesAll ? null : (
        <Alert>
          You see your own actions. Compliance and auditors see everyone's.
        </Alert>
      )}
      {canVerify ? <ChainCard /> : null}
      <Card className="px-5">
        <form
          className="grid gap-3 md:grid-cols-3 xl:grid-cols-6"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({ search: cleaned(draft) });
          }}
        >
          {seesAll ? (
            <Select
              label="Staff"
              value={draft.staff_id ?? ''}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  staff_id: event.target.value || undefined,
                })
              }
            >
              <option value="">Everyone</option>
              {staff.data?.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.display_name}
                </option>
              ))}
            </Select>
          ) : null}
          <CustomerFilter
            value={draft.customer_id}
            onChange={(customerId) =>
              setDraft({ ...draft, customer_id: customerId })
            }
          />
          <Select
            label="Action"
            value={draft.action ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, action: event.target.value || undefined })
            }
          >
            <option value="">Any action</option>
            {draft.action && !known ? (
              <option value={draft.action}>{draft.action}</option>
            ) : null}
            {actions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <DatePicker
            label="From"
            value={draft.from ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, from: event.target.value || undefined })
            }
          />
          <DatePicker
            label="To"
            value={draft.to ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, to: event.target.value || undefined })
            }
          />
          <div className="flex items-end gap-2">
            <Button type="submit">Filter</Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDraft({});
                void navigate({ search: {} });
              }}
            >
              Clear
            </Button>
          </div>
        </form>
      </Card>
      <Checkbox
        checked={search.reads !== 'all'}
        onCheckedChange={(hide) => {
          const next = {
            ...search,
            reads: hide ? undefined : ('all' as const),
          };
          setDraft((current) => ({ ...current, reads: next.reads }));
          void navigate({ search: next });
        }}
      >
        Hide routine reads (page views and lookups)
      </Checkbox>
      {page.error ? (
        <Alert variant="destructive">{page.error.message}</Alert>
      ) : null}
      <AuditTable
        rows={page.rows}
        more={page.more}
        empty="Nothing matches these filters."
      />
    </>
  );
}

/** "Audit chain verified · checked 2 min ago", or the danger variant. */
function ChainCard() {
  const verify = useQuery(adminQuery('audit.verify', {}));
  const check = verify.data;
  if (!check) return null;
  const ok = check.chain_ok;
  const checked = relativeTime(new Date(verify.dataUpdatedAt).toISOString());
  return (
    <section
      aria-label="Audit chain"
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-4 py-2.5 text-sm',
        ok
          ? 'border-border bg-card'
          : 'border-danger-border bg-danger-tint text-danger-ink',
      )}
    >
      <Icon
        icon={ok ? CheckmarkCircle02Icon : Alert02Icon}
        size={18}
        className={ok ? 'text-success-ink' : undefined}
      />
      <p className={cn('min-w-0 flex-1', !ok && 'font-medium')}>
        {ok
          ? `Audit chain verified · checked ${checked}`
          : `Audit chain broken at entry ${check.broken_at?.toLocaleString('en-GB') ?? 'unknown'} · checked ${checked}`}
      </p>
    </section>
  );
}

function cleaned(search: AuditSearch): AuditSearch {
  return Object.fromEntries(
    Object.entries(search).filter(
      ([, value]) => value !== undefined && value !== '',
    ),
  );
}
